 import { Card, CardContent, useTheme, Avatar, Stack, Box } from "@mui/material";
import { CMChip } from "../common/CMChip";
import { keyframes } from "@mui/system";
import React, { useState } from "react";
import { RelatedEntityItem } from "../../types";
import { getSafeThumbnailUrl } from "../../utilities/mediaUtils";
import { createThemeColorGenerator } from "../../utilities/themeColorGenerator";

interface RelatedItemCardProps {
  entity: RelatedEntityItem;
  category: string;
  position: {
    x: number;
    y: number;
  };
  onClick?: () => void;
  isDirect: boolean;
  entityType: 'geo' | 'related' | 'media';
}

// Define fade animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

// Flag to control visibility of relationship line/text
const SHOW_RELATIONSHIP_VISUALS = false;

const RelatedItemCard: React.FC<RelatedItemCardProps> = ({
  entity,
  category,
  position,
  onClick,
  isDirect,
  entityType,
}) => {
  const theme = useTheme();
  const themeColors = React.useMemo(() => createThemeColorGenerator(theme), [theme]);
  const [isHovered, setIsHovered] = useState(false);

  // FIXME: Backend API inconsistency
  // Different endpoints return different field names for the same concepts:
  // - Most endpoints use entity_name, entity_description, rel_description
  // - Groups endpoint uses name, description
  // - Some endpoints don't include rel_description
  // This normalization handles these inconsistencies until the API is standardized
  
  // Use the same efficient name extraction logic as the main details view
  // This ensures consistency without additional network requests
  const getEntityDisplayName = (entity: RelatedEntityItem, category: string): string => {
    // Priority order matches the main getDataItemName logic:
    // 1. entity_name (from relationship resolution)
    // 2. name (fallback)
    // 3. Category-based fallback
    
    if (entity?.entity_name && typeof entity.entity_name === 'string' && entity.entity_name.trim()) {
      const trimmed = entity.entity_name.trim();
      // Skip generic/poor names that are just timestamps or IDs
      if (!trimmed.match(/^\d{1,2}:\d{2}$/) && // Not just time like "11:23"
          !trimmed.match(/^[0-9a-f-]{8,}$/i) && // Not just UUID fragments
          trimmed !== 'Unknown Item' && 
          trimmed !== category) {
        return trimmed;
      }
    }
    
    if (entity?.name && typeof entity.name === 'string' && entity.name.trim()) {
      const trimmed = entity.name.trim();
      if (trimmed !== 'Unknown Item' && trimmed !== category) {
        return trimmed;
      }
    }
    
    // Fallback to category with ID
    return `${category} ${entity.id.toString().substring(0, 8)}`;
  };

  const entityName = getEntityDisplayName(entity, category);
  const relationshipDescription = entity.rel_description || "";
  const thumbnailUrl = React.useMemo(() => getSafeThumbnailUrl(entity), [entity]);
  
  // Log when we had to use fallback names for debugging (only in development)
  if (process.env.NODE_ENV === 'development' && entityName.includes(category) && entityName.includes(entity.id.toString().substring(0, 8))) {
    console.info(`🔍 [RELATED-CARD] Filtered poor quality name "${entity?.entity_name}" → "${entityName}" for ${category}`);
  }

  // Calculate points for the relationship bar
  const barHeight = 24;
  const lineLength = Math.sqrt(
    position.x * position.x + position.y * position.y
  );
  const perpX = ((-position.y / lineLength) * barHeight) / 2;
  const perpY = ((position.x / lineLength) * barHeight) / 2;

  const polygonPoints = `
    ${window.innerWidth / 2 + perpX},${window.innerHeight / 2 + perpY}
    ${window.innerWidth / 2 - perpX},${window.innerHeight / 2 - perpY}
    ${window.innerWidth / 2 + position.x - perpX},${
    window.innerHeight / 2 + position.y - perpY
  }
    ${window.innerWidth / 2 + position.x + perpX},${
    window.innerHeight / 2 + position.y + perpY
  }
  `;

  // Note: Using consistent MediaDrawer-style borders for all entity types

  return (
    <>
      {/* Conditionally render relationship line/text */} 
      {SHOW_RELATIONSHIP_VISUALS && (
        <svg
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            width: "100vw",
            height: "100vh",
            pointerEvents: "none",
            zIndex: 9999,
            opacity: isHovered ? 1 : 0,
            animation: `${isHovered ? fadeIn : fadeOut} 0.15s ease-in-out forwards`,
            visibility: isHovered ? "visible" : "hidden",
          }}
        >
          <defs>
            <path
              id={`relationPath-${entity.id}`}
              d={`M ${window.innerWidth / 2},${window.innerHeight / 2} L ${
                window.innerWidth / 2 + position.x
              },${window.innerHeight / 2 + position.y}`}
            />
          </defs>

          {/* Background bar */}
          <polygon
            points={polygonPoints}
            fill={theme.palette.background.paper}
            opacity="0.9"
          />

          {/* Border lines */}
          <polygon
            points={polygonPoints}
            fill="none"
            stroke={theme.palette.secondary.main}
            strokeWidth="1"
            opacity="0.3"
          />

          {relationshipDescription && (
            <text
              dy="0"
              fill={theme.palette.primary.main}
              fontSize="14px"
              textAnchor="middle"
              style={{
                fontWeight: 500,
              }}
            >
              <textPath href={`#relationPath-${entity.id}`} startOffset="50%">
                {relationshipDescription}
              </textPath>
            </text>
          )}
        </svg>
      )}

      {/* Card */}
      <Card
        onClick={() => {
          console.log('[RelatedItemCard] Card clicked:', {
            entityId: entity.id,
            entityName: entityName,
            category,
            entityType,
            hasOnClick: !!onClick
          });
          onClick?.();
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          height: isDirect ? 75 : 50,
          width: isDirect ? 75 : 50,
          cursor: "pointer",
          pointerEvents: "auto",
          backgroundColor: "rgba(26, 26, 26, 0.9)", // Modern dark background like MediaDrawer
          color: "white", // Ensure white text for readability
          backdropFilter: "blur(8px)", // Modern blur effect
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)", // Modern shadow
          border: `5px solid ${themeColors.byHash(category)}`,
          borderRadius: "1000px", // circle
          overflow: "visible",          
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))` + (isHovered ? ` scale(2)` : ""),
          transition: "all 0.1s ease-out", // quick snap back
          "&:hover": {
            border: `3px solid ${themeColors.byHash(category)}`,
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)", // Enhanced shadow on hover
            transition: "transform 0.3s ease-in", // smooth easing in
            zIndex: 10,
          },
          // Performance optimizations
          willChange: "transform",
          backfaceVisibility: "hidden",
        }}
      >
        <CardContent
          sx={{ 
            pt: 1, px: 1, 
            pb: '2px',
            width: "100%",
            "&:last-child": { 
              pb: '2px'
            } 
          }}
        >
          <Stack spacing={0.5} alignItems="center">
            <CMChip
              label={category.toUpperCase()}
              colorScheme="default"
              size="small"
              sx={{
                display: isHovered ? "flex" : "none", // Use flex for centering
                alignItems: "center", // Vertical centering
                justifyContent: "center", // Horizontal centering
                position: "absolute",
                top: "-8px",
                transform: "translateY(-100%)",
                zIndex: 999,
                fontSize: "0.6rem",
                height: "20px",
                minWidth: "auto",
                margin: 0,
                px: 1,
                backgroundColor: themeColors.byHash(category),
                color: "white", // Force white text for better contrast
                borderRadius: "4px", // Standard corner radius
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)", // Modern shadow
                backdropFilter: "blur(8px)", // Modern blur effect
                transition: "all 0.2s ease-in-out", // Smooth transitions
                "&:hover": {
                  transform: "translateY(-100%) scale(1.05)", // Slight scale on hover
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.2)", // Enhanced shadow on hover
                },
                "& .MuiChip-label": {
                  px: 2, // More horizontal padding
                  py: 1, // More vertical padding
                  fontSize: "0.5rem", // Smaller text
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700, // Bold for category type
                }
              }}
            />

            {/* Thumbnail Avatar */}
            {thumbnailUrl ? (
              <Avatar
                src={thumbnailUrl}
                alt={`${entityName} thumbnail`}
                sx={{
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  height: "100%",
                  overflow: "hidden",
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "100%"
                }}
                imgProps={{ loading: 'lazy' }}
              />) : (
                <Avatar
                  alt={`${entityName} thumbnail`}
                  sx={{
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                    height: "100%",
                    overflow: "hidden",
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "100%"
                  }}
                >
                  <Box sx={{
                    left: "50%", 
                    maxHeight: "64%", 
                    overflowY: "hidden", 
                    position: "absolute", 
                    textAlign: "center", 
                    top: "50%", 
                    transform: "translate(-50%, -50%)", 
                    width: "64%", 
                    wordBreak: "break-all"
                  }}>
                    {entityName}
                  </Box>
                </Avatar>                
              )
            }

            {/* Entity Name */}
            <CMChip
              label={entityName}
              colorScheme="default"
              size="small"
              sx={{
                display: isHovered ? "flex" : "none", // Use flex for centering
                alignItems: "center", // Vertical centering
                justifyContent: "center", // Horizontal centering
                position: "absolute",
                bottom: "-8px",
                transform: "translateY(100%)",
                zIndex: 999,
                fontSize: "0.65rem",
                height: "auto", // Allow height to grow with content
                minHeight: "24px",
                minWidth: "auto",
                margin: 0,
                maxWidth: "120px", // Increased max width
                backgroundColor: themeColors.byHash(category),
                color: "white", // Force white text for better contrast
                borderRadius: "4px", // Standard corner radius
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)", // Modern shadow
                backdropFilter: "blur(8px)", // Modern blur effect
                transition: "all 0.2s ease-in-out", // Smooth transitions
                "&:hover": {
                  transform: "translateY(100%) scale(1.05)", // Slight scale on hover
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.2)", // Enhanced shadow on hover
                },
                "& .MuiChip-label": {
                  px: 2, // More horizontal padding
                  py: 1, // More vertical padding
                  fontSize: "0.55rem", // Smaller text
                  lineHeight: 1.2,
                  whiteSpace: "normal", // Allow wrapping
                  wordBreak: "break-word", // Break long words if needed
                  textAlign: "center",
                  maxWidth: "110px", // Slightly less than chip max width
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 400, // Normal weight
                  fontStyle: "italic", // Italicized for entity name
                }
              }}
            />
          </Stack>
        </CardContent>
      </Card>
    </>
  );
};

export default RelatedItemCard;
